/** Deal contractors & payments — v1's per-deal contractor budget, stored as the
 * org-settings blob key `TXN_CONTRACTORS` (ONE array across all deals, each entry
 * tagged with its `deal` address). Shape mined live 2026-07-18:
 *   { deal, name, type, desc, total, paid,
 *     payments:  [{ date, amount, method, notes }],
 *     installments: [{ amount, paid }] }
 * READ is Phase A. WRITE (add contractor / log payment / toggle installment)
 * rides the settings door (PHASE18 whitelists TXN_CONTRACTORS) and is gated by
 * liveFire `contractorsEdit`. */
import type { RecOp } from "../../lib/outbox";
import { natKey } from "../../lib/natkey";
import { withLedgerBase } from "../settings/api";

export interface ContractorPayment { date: string; amount: number; method: string; notes: string; }
export interface ContractorInstallment { amount: number; paid: boolean; }
export interface ContractorChangeOrder { amount: number; reason: string; }
/** Structured payout details (v1 openPayInfo/_PD_FIELDS): a method plus a
 * method-keyed detail map (wire beneficiary/routing/account, Zelle handle, ACH,
 * check payable-to…). v1 stores `pay = { method, details:{ <fieldKey>: value } }`. */
export interface ContractorPayInfo { method: string; details: Record<string, string>; }

/** Per-method payout-detail field registry — verbatim from v1 txn-expenses.js
 * `_PD_FIELDS`: [fieldKey, label, placeholder]. Drives the Pay-to form and the
 * card summary so v2 round-trips exactly the keys v1 writes. */
export const PD_FIELDS: Record<string, [string, string, string][]> = {
  Zelle: [["zelle", "Zelle Email / Phone", "name@email.com or 555-555-5555"]],
  Venmo: [["venmo", "Venmo Handle", "@username"]],
  Wire: [
    ["benName", "Beneficiary Name (as on the account)", "John Smith / ABC LLC"],
    ["benAddr", "Beneficiary Address", "street, city, state, zip"],
    ["bankName", "Bank Name", "e.g. Chase"],
    ["bankAddr", "Bank Address (optional)", ""],
    ["routing", "ABA Routing Number (9 digits)", "021000021"],
    ["account", "Account Number", ""],
    ["acctType", "Account Type", "Checking or Savings"],
    ["swift", "SWIFT / BIC (international only)", ""],
    ["ref", "Reference / Memo (optional)", ""],
  ],
  ACH: [["routing", "ABA Routing Number (9 digits)", ""], ["account", "Account Number", ""], ["acctType", "Account Type", "Checking or Savings"]],
  Check: [["payableTo", "Payable To", ""], ["mailAddr", "Mailing Address", ""]],
  Cash: [["notes", "Notes (optional)", ""]],
  Card: [["notes", "Card / notes (optional)", ""]],
  Other: [["notes", "Details", ""]],
};

/** One-line summary of a payout method (v1 `_pdSummary`): method · <the salient
 * field>, masking the account number for ACH. */
export function pdSummary(pay: ContractorPayInfo | null): string {
  if (!pay || !pay.method) return "";
  const d = pay.details || {};
  const m = pay.method;
  const bit =
    m === "Zelle" ? d["zelle"] :
    m === "Venmo" ? d["venmo"] :
    m === "Wire" ? (d["bankName"] || "") :
    m === "ACH" ? (d["routing"] ? "••" + String(d["account"] || "").slice(-4) : "") :
    m === "Check" ? (d["payableTo"] || "") : "";
  return m + (bit ? " · " + bit : "");
}
export interface Contractor {
  deal: string;
  name: string;
  type: string;
  desc: string;
  total: number;
  paid: number;
  payments: ContractorPayment[];
  installments: ContractorInstallment[];
  changeOrders: ContractorChangeOrder[];
  pay: ContractorPayInfo | null;
}

const num = (v: unknown): number => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};
const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const streetKey = (a: string): string => (natKey(a).split(",")[0] ?? "").trim();

export function narrowContractor(raw: unknown): Contractor {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const payments = Array.isArray(o["payments"]) ? (o["payments"] as unknown[]) : [];
  const installments = Array.isArray(o["installments"]) ? (o["installments"] as unknown[]) : [];
  const changeOrders = Array.isArray(o["changeOrders"]) ? (o["changeOrders"] as unknown[]) : [];
  const pay = narrowPayInfo(o["pay"]);
  return {
    deal: str(o["deal"]),
    name: str(o["name"]),
    type: str(o["type"]),
    desc: str(o["desc"]),
    total: num(o["total"]),
    paid: num(o["paid"]),
    payments: payments.map((p) => {
      const q = p && typeof p === "object" ? (p as Record<string, unknown>) : {};
      return { date: str(q["date"]), amount: num(q["amount"]), method: str(q["method"]), notes: str(q["notes"]) };
    }),
    installments: installments.map((i) => {
      const q = i && typeof i === "object" ? (i as Record<string, unknown>) : {};
      return { amount: num(q["amount"]), paid: q["paid"] === true };
    }),
    changeOrders: changeOrders.map((c) => {
      const q = c && typeof c === "object" ? (c as Record<string, unknown>) : {};
      return { amount: num(q["amount"]), reason: str(q["reason"]) };
    }).filter((c) => c.amount !== 0 || c.reason),
    pay,
  };
}

/** Narrow a raw `pay` blob to {method, details:{…}} or null when empty. Only the
 * fields v1 declares for the chosen method are retained; blank values dropped. */
export function narrowPayInfo(raw: unknown): ContractorPayInfo | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const method = str(o["method"]);
  const dRaw = o["details"] && typeof o["details"] === "object" && !Array.isArray(o["details"])
    ? (o["details"] as Record<string, unknown>) : {};
  const details: Record<string, string> = {};
  for (const [k] of PD_FIELDS[method] ?? []) {
    const v = str(dRaw[k]).trim();
    if (v) details[k] = v;
  }
  // preserve any stored keys outside the registry too (forward-compat)
  for (const k of Object.keys(dRaw)) {
    if (!(k in details)) { const v = str(dRaw[k]).trim(); if (v) details[k] = v; }
  }
  return method || Object.keys(details).length ? { method, details } : null;
}

/** The whole TXN_CONTRACTORS array (raw objects), from orgSettings. */
export function allContractorsRaw(orgSettings: Record<string, unknown>): Record<string, unknown>[] {
  const v = orgSettings["TXN_CONTRACTORS"];
  return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
}

/** Contractors on ONE deal — full-address match, street-key fallback (v1 blobs
 * can carry slightly different address formatting than rec_deals). */
export function contractorsForDeal(orgSettings: Record<string, unknown>, address: string): Contractor[] {
  const want = natKey(address);
  const wantStreet = streetKey(address);
  const out: Contractor[] = [];
  for (const r of allContractorsRaw(orgSettings)) {
    const c = narrowContractor(r);
    const dk = natKey(c.deal);
    if (dk === want || (wantStreet && streetKey(c.deal) === wantStreet)) out.push(c);
  }
  return out;
}

/** Contract total. NOTE v1 folds each approved change order INTO `total` at
 * add-time (openChangeOrder: `v.total += amt`), so `total` is already the
 * CO-inclusive contract amount — we must NOT re-add changeOrders here or existing
 * v1 data double-counts. `changeOrders` is retained purely as display history. */
export const contractTotal = (c: Contractor): number => c.total;

/** Sum of approved change orders (display only — already inside `total`). */
export const changeOrderTotal = (c: Contractor): number =>
  c.changeOrders.reduce((n, o) => n + o.amount, 0);

export const balanceOf = (c: Contractor): number => contractTotal(c) - c.paid;

export function dealTotals(list: Contractor[]): { total: number; paid: number; balance: number } {
  const total = list.reduce((n, c) => n + contractTotal(c), 0);
  const paid = list.reduce((n, c) => n + c.paid, 0);
  return { total, paid, balance: total - paid };
}

/** Split a total into n installments — v1 submitVendor: `each = round(total/n)`,
 * n equal copies, no remainder correction (matches v1 stored amounts exactly). */
export function splitInstallments(total: number, n: number): ContractorInstallment[] {
  if (n <= 0 || total <= 0) return [];
  const each = Math.round(total / n);
  return Array.from({ length: n }, () => ({ amount: each, paid: false }));
}

/** Common option lists — datalist hints only; any stored value is preserved. */
export const CONTRACTOR_TYPES = [
  "General Contractor", "Plumber", "Electrician", "HVAC", "Roofer", "Painter",
  "Flooring", "Landscaper", "Handyman", "Inspector", "Other",
];
export const PAY_METHODS = ["Zelle", "Wire", "Venmo", "ACH", "Check", "Cash", "Card", "Other"];

/** Settings-door put of the WHOLE contractors array (v1 replaces the blob
 * wholesale; the door whitelists TXN_CONTRACTORS as of PHASE18). */
export function opPutContractors(all: Record<string, unknown>[]): RecOp {
  return withLedgerBase({ s: "settings", op: "put", k: "TXN_CONTRACTORS", d: all });
}

/** Serialize a Contractor back to the v1 blob shape (round-trips unknown-free —
 * we only ever hold the fields v1 uses). */
export function contractorToRaw(c: Contractor): Record<string, unknown> {
  return {
    deal: c.deal, name: c.name, type: c.type, desc: c.desc,
    total: c.total, paid: c.paid,
    payments: c.payments.map((p) => ({ date: p.date, amount: p.amount, method: p.method, notes: p.notes })),
    installments: c.installments.map((i) => ({ amount: i.amount, paid: i.paid })),
    changeOrders: c.changeOrders.map((o) => ({ amount: o.amount, reason: o.reason })),
    ...(c.pay ? { pay: { method: c.pay.method, details: { ...c.pay.details } } } : {}),
  };
}

export const CONTRACTOR_MSG = {
  editPhaseB: "Adding contractors and logging payments unlocks at go-live",
  none: "No contractors on this deal yet.",
  saveFail: "Couldn't save — nothing changed",
};
